import { css } from 'aphrodite/no-important';
import { QuickLinks } from 'components';
import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isExhibitorUser } from 'redux/reducers/user';
import { RootState } from 'redux/store';
import { ROUTE } from 'util/constants';
import { checkIfTrue, makeSlug } from 'util/helpers';
import { Branding, BrandingType } from './Branding/Branding';
import { MobileNav } from './MobileNav/MobileNav';
import { ISubLink, NavLinkItem } from './NavLinkItem/NavLinkItem';
import { style } from './style';
import { WelcomeSection } from './WelcomeSection/WelcomeSection';

interface Props {
  windowStatus: boolean;
  onLogout: () => void;
}
interface LocationState {
  from: {
    pathname: string;
  };
}

export const Nav: FC<Props> = ({ onLogout, windowStatus }) => {
  const cmsData = useSelector((state: RootState) => state.widget.cms.text.header);
  const userData = useSelector((state: RootState) => state.user.data);
  const isExhibitor = useSelector(isExhibitorUser);
  const quickLinks = useSelector((state: RootState) => state.widget.cms.text.floating_quick_links);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { logo_url, header_text, navigation } = cmsData;
  const location = useLocation<LocationState>();
  const staticNavPages = [ROUTE.HOME, ROUTE.CLOSED] as string[]; // pages that don't show all nav links
  const isNavStatic = staticNavPages.includes(location.pathname);
  const isVerticalBranding = location.pathname === ROUTE.HOME;

  const styles = style({ isMenuOpen, isUserLoggedIn: !!userData, windowStatus, isVerticalBranding, isNavStatic });

  const handleLinkClick = (e: any) => {
    if (e && e.target.tagName.toLowerCase() === 'a') {
      setIsMenuOpen(false);
    }
    window.scrollTo(0, 0);
  };

  const buildNav = () => {
    // we only need to show the Branding component if the user is in the Closed or Home Page
    if (isNavStatic) return <></>;

    return (
      <>
        {getHeaderNavLinks()}
        {getMobileDropdownNav()}
        <WelcomeSection key='welcome-section' onLogout={onLogout} />
      </>
    );
  };

  const hasDropdown = (route: string) => {
    switch (route) {
      case '/partner-pavilion':
      case '/innovation-zone':
        return true;
      default:
        return false;
    }
  };

  const getDropdownOptionsByRoute = (route: string): string[] => {
    switch (route) {
      case '/partner-pavilion':
        return (cmsData.partner_categories as string[]) || [];
      case '/innovation-zone':
        return (cmsData.innovation_zone_categories as string[]) || [];
      case '/technical-tracks':
        return (cmsData.technical_tracks_categories as string[]) || [];
      default:
        return [];
    }
  };

  const getNavDropdown = (id: number, link: any, options: string[]): JSX.Element => {
    const sublinks: ISubLink[] = options.map((option) => {
      return { text: option, route: `${link.route}/${makeSlug(option)}` };
    });
    return (
      <NavLinkItem key={id} link={link} sublinks={sublinks} isVisibleOnMobile={checkIfTrue(link.on_mobile_header)} />
    );
  };

  const getHeaderNavLinks = (): JSX.Element[] => {
    return navigation
      .filter((link: any) => {
        return !!link.text && !!link.route;
      })
      .filter((link: any) => {
        return !isExhibitor || link.route !== ROUTE.SURVEY;
      })
      .map((link: any, index: number) => {
        if (!hasDropdown(link.route)) {
          return (
            <NavLinkItem key={index.toString()} link={link} isVisibleOnMobile={checkIfTrue(link.on_mobile_header)} />
          );
        }
        const options = getDropdownOptionsByRoute(link.route);
        return getNavDropdown(index, link, options);
      });
  };

  const getMobileDropdownNav = (): JSX.Element => {
    return (
      <>
        {isMenuOpen && <MobileNav toggleMenu={setIsMenuOpen} onLogout={onLogout} />}
        {getHamburgerButton(isMenuOpen)}
      </>
    );
  };

  const getHamburgerButton = (open: boolean): JSX.Element => {
    //Note: Only displayed when viewport < mobile
    return (
      <div className={css(styles.hamburger, open && styles.activeHamburger)} onClick={() => setIsMenuOpen(!open)}>
        <span /> <span /> <span />
      </div>
    );
  };

  return (
    <header className={css(styles.header)}>
      <nav className={css(styles.nav)} onClick={(e) => handleLinkClick(e)}>
        <Branding
          type={isVerticalBranding ? BrandingType.VERTICAL : BrandingType.HORIZONTAL}
          logoUrl={logo_url}
          copy={header_text}
          isVisible={!isMenuOpen}
          isCopyStatic={false}
        />
        {buildNav()}
        {windowStatus && <QuickLinks cmsData={quickLinks} />}
      </nav>
    </header>
  );
};
